import { useCallback, useEffect, useState } from "react";
import { Accessibility, X } from "lucide-react";
import api from "@/lib/api";

const STORE_KEY = "sp_a11y";
const DEFAULTS = { dyslexia: false, contrast: false, targets: false, eyegaze: false, voice: false };
const VOICE_SUPPORTED = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

function apply(prefs) {
  const root = document.documentElement;
  root.classList.toggle("a11y-dyslexia", !!prefs.dyslexia);
  root.classList.toggle("a11y-contrast", !!prefs.contrast);
  root.classList.toggle("a11y-targets", !!prefs.targets);
  root.classList.toggle("a11y-eyegaze", !!prefs.eyegaze);
  if (prefs.dyslexia && !document.getElementById("opendyslexic-cdn")) {
    const link = document.createElement("link");
    link.id = "opendyslexic-cdn";
    link.rel = "stylesheet";
    link.href = "https://fonts.cdnfonts.com/css/opendyslexic";
    document.head.appendChild(link);
  }
  window.dispatchEvent(new Event("sp-a11y-changed"));
}

export default function AccessibilityDrawer() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULTS);

  useEffect(() => {
    const saved = localStorage.getItem(STORE_KEY);
    if (saved) {
      try {
        const parsed = { ...DEFAULTS, ...JSON.parse(saved) };
        setPrefs(parsed);
        apply(parsed);
        return;
      } catch { /* fall through to profile flags */ }
    }
    // No manual prefs yet — mirror the participant's registered accessibility profile on login
    if (sessionStorage.getItem("sp_a11y_flags_done")) return;
    sessionStorage.setItem("sp_a11y_flags_done", "1");
    if (!localStorage.getItem("nc_token")) return; // anonymous visitors have no profile to mirror
    api.get("/ndis/my-flags").then((r) => {
      const profiles = r.data?.accessibility_profiles || [];
      if (!profiles.length) return;
      const mapped = {
        dyslexia: false,
        contrast: profiles.includes("screen_reader"),
        targets: profiles.includes("lite_interface"),
        eyegaze: profiles.includes("voice_control"),
        voice: profiles.includes("voice_control"),
      };
      if (mapped.contrast || mapped.targets || mapped.eyegaze || mapped.voice) {
        setPrefs(mapped);
        apply(mapped);
      }
    }).catch(() => {});
  }, []);

  const toggle = useCallback((key) => {
    setPrefs((p) => {
      const next = { ...p, [key]: !p[key] };
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
      apply(next);
      return next;
    });
  }, []);

  const OPTIONS = [
    ["dyslexia", "Dyslexia-friendly font", "OpenDyslexic typeface for reading legibility"],
    ["contrast", "High-contrast mode", "Strict solid colours above 4.5:1 contrast"],
    ["targets", "Enhanced target spacing", "48px minimum controls for fine-motor support"],
    ["eyegaze", "Eye-tracking assist", "64px dwell-friendly targets, hover outlines, calmer motion — built for Tobii-style gaze and switch hardware"],
    ["voice", "Voice control", VOICE_SUPPORTED ? "Speak to navigate: 'scroll down', 'click submit', 'focus email', 'type …'" : "Not supported in this browser — use Chrome or Edge for voice control"],
  ];

  return (
    <div className="fixed top-20 right-4 z-[90] flex flex-col items-end">
      <button
        data-testid="a11y-drawer-toggle"
        aria-label="Open accessibility settings"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="border border-white/20 bg-black/80 backdrop-blur p-3 text-zinc-300 hover:text-white hover:border-white/50 transition-colors"
      >
        {open ? <X className="w-4 h-4" /> : <Accessibility className="w-4 h-4" />}
      </button>
      {open && (
        <div data-testid="a11y-drawer-panel" role="dialog" aria-label="Accessibility settings" className="mt-2 w-80 border border-white/15 bg-[#0a0a0a] shadow-2xl">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase px-4 py-3 border-b border-white/10">Accessibility Settings</p>
          {OPTIONS.map(([key, label, hint]) => (
            <label key={key} className={`flex items-start gap-3 px-4 py-3 border-b border-white/5 transition-colors ${key === "voice" && !VOICE_SUPPORTED ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:bg-white/5"}`}>
              <input
                type="checkbox"
                data-testid={`a11y-toggle-${key}`}
                checked={prefs[key]}
                disabled={key === "voice" && !VOICE_SUPPORTED}
                onChange={() => toggle(key)}
                className="w-4 h-4 mt-0.5 accent-[#2E6BFF] shrink-0"
              />
              <span>
                <span className="font-sans font-semibold text-xs text-zinc-100 block">{label}</span>
                <span className="font-mono text-[9px] text-zinc-500 block mt-0.5">{hint}</span>
              </span>
            </label>
          ))}
          <p className="font-mono text-[8px] text-zinc-600 px-4 py-3">Saved on this device. Registered NDIS participants: your profile settings apply automatically on login.</p>
        </div>
      )}
    </div>
  );
}
